import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Card } from '@components/atoms/Card';
import { Table } from '@components/atoms/Table';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { InputAmount } from '@components/atoms/InputAmount';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { SITE_URLS } from '@constants';
import { ProductAvailability } from '@types';
import { formatPrice } from '@utils/format';
import { useProductAvailability } from '@hooks/useProductAvailability';
import { useUpdateProductInCart } from '@services/useUpdateProductInCart';
import styles from './ProductTable.module.css';
import { PopupModifyCartItem } from '../PopupModifyCartItem';
import { useThemes } from '@hooks/useThemes';

export interface ProductTableProps {
    merchant?: string;
    cartItems: {
        uuid?: string;
        product?: {
            uuid?: string;
            name?: string;
            image?: string;
            hasAttributes?: boolean;
        };
        price?: number;
        quantity?: number;
        attributes?: { uuid?: string; value?: string }[];
        availability: ProductAvailability;
    }[];
}

export const ProductTable: FC<ProductTableProps> = ({ merchant, cartItems }) => {
    const { useGrayscaleTheme } = useThemes();
    const { translateAvailability, getAvailabilityTypographyColor } = useProductAvailability();
    const { t } = useTranslation();

    const translations = {
        merchantLabel: t('product-table-component.merchant.label'),
        productLabel: t('product-table-component.product.label'),
        quantityLabel: t('product-table-component.quantity.label'),
        priceLabel: t('product-table-component.price.label'),
    };

    // id of the cart item to display
    const [modifyCartItemModal, setModifyCartItemModal] = useState<string | null>(null);

    return (
        <div className={`${useGrayscaleTheme ? styles['theme-grayscale'] : ''}`}>
            <Typography classes="mb-3" fontSize="16">
                <>
                    {`${translations.merchantLabel} `}
                    <Typography fontWeight="800" tagVariant={TypographyTagVariants.span}>
                        {merchant}
                    </Typography>
                </>
            </Typography>

            <Card>
                <Table
                    borderless={true}
                    hideHeaderOnMobile={true}
                    headerCols={[
                        { classes: 'text-start', text: translations.productLabel },
                        { classes: 'text-center', text: translations.quantityLabel, fit: true },
                        { classes: `${styles['price-col']} text-end`, text: translations.priceLabel },
                    ]}
                    contentRows={cartItems.flatMap((cartItem, index) => {
                        return [
                            // product information
                            [
                                {
                                    content: (
                                        <>
                                            <div className="d-flex gap-3 align-items-start text-decoration-none my-4 mb-3 mb-md-4">
                                                <div>
                                                    {cartItem.product?.image && (
                                                        <img
                                                            className={styles['product-image']}
                                                            src={cartItem.product?.image}
                                                            alt={cartItem.product?.name}
                                                        />
                                                    )}
                                                </div>
                                                <div className="d-grid">
                                                    <Anchor
                                                        href={SITE_URLS.productDetail.replace(
                                                            ':uuid',
                                                            `${cartItem.product?.uuid}`
                                                        )}
                                                        mode={AnchorMode.none}>
                                                        <Typography
                                                            classes="mt-2 mb-1 pt-1"
                                                            color="dark"
                                                            fontSize="14"
                                                            fontWeight="900"
                                                            breakpointsStyles={{ md: { fontSize: '16' } }}>
                                                            {cartItem.product?.name}
                                                        </Typography>
                                                    </Anchor>
                                                    <Typography
                                                        classes="text-truncate mb-1"
                                                        color={getAvailabilityTypographyColor(cartItem.availability)}
                                                        fontSize="14">
                                                        {translateAvailability(cartItem.availability)}
                                                    </Typography>
                                                    {cartItem.attributes?.map((attribute) => (
                                                        <Typography
                                                            key={attribute.uuid}
                                                            classes="mb-1"
                                                            color="dark"
                                                            fontSize="14"
                                                            fontWeight="400"
                                                            breakpointsStyles={{ md: { fontSize: '16' } }}>
                                                            {attribute.value}
                                                        </Typography>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="d-flex d-md-none justify-content-between align-items-center me-3 my-3">
                                                <div className="d-flex gap-3">
                                                    <ProductAmount uuid={cartItem.uuid || ''} quantity={cartItem.quantity || 0} />
                                                    <div className="d-flex gap-2">
                                                        {cartItem.product?.hasAttributes && (
                                                            <ModifyCartItem
                                                                uuid={cartItem.uuid || ''}
                                                                compact={true}
                                                                setModal={setModifyCartItemModal}
                                                            />
                                                        )}
                                                        <RemoveCartItem uuid={cartItem.uuid || ''} compact={true} />
                                                    </div>
                                                </div>
                                                <Typography
                                                    classes={`${styles.price} mb-0`}
                                                    align="right"
                                                    fontWeight="800"
                                                    fontSize="24"
                                                    styles={
                                                        cartItem.availability === ProductAvailability.outOfStock
                                                            ? { lineThrough: true }
                                                            : {}
                                                    }>
                                                    {formatPrice(cartItem.price)}
                                                </Typography>
                                            </div>
                                        </>
                                    ),
                                },
                                // hide 2nd and 3rd cells on mobile
                                {
                                    classes: 'd-none d-md-table-cell align-content-start pt-5',
                                    content: (
                                        <>
                                            <ProductAmount uuid={cartItem.uuid || ''} quantity={cartItem.quantity || 0} />
                                            {cartItem.product?.hasAttributes && (
                                                <div className="pt-4 mt-5">
                                                    <ModifyCartItem
                                                        uuid={cartItem.uuid || ''}
                                                        setModal={setModifyCartItemModal}
                                                    />
                                                </div>
                                            )}
                                        </>
                                    ),
                                },
                                {
                                    classes: `${styles['price-col']} d-none d-md-table-cell align-content-start pt-5`,
                                    content: (
                                        <>
                                            <Typography
                                                classes={`${styles.price} pb-1 mb-0`}
                                                fontWeight="800"
                                                fontSize="24"
                                                align="right"
                                                styles={
                                                    cartItem.availability === ProductAvailability.outOfStock
                                                        ? { lineThrough: true }
                                                        : {}
                                                }>
                                                {formatPrice(cartItem.price)}
                                            </Typography>
                                            <div className="d-flex justify-content-end pt-4 mt-5">
                                                <RemoveCartItem uuid={cartItem.uuid || ''} />
                                            </div>
                                        </>
                                    ),
                                },
                            ],
                            // line separator (except on last line)
                            index !== cartItems.length - 1 ? [{ colspan: 3, content: <hr /> }] : [],
                        ];
                    })}
                />
            </Card>

            <PopupModifyCartItem
                cartItemUuid={modifyCartItemModal}
                show={modifyCartItemModal !== null}
                onHide={() => setModifyCartItemModal(null)}
            />
        </div>
    );
};

interface ProductAmountProps {
    uuid: string;
    quantity: number;
}

const ProductAmount: FC<ProductAmountProps> = ({ uuid, quantity }) => {
    const { request: requestUpdateProductInCart } = useUpdateProductInCart();

    return (
        <InputAmount
            min={0}
            value={quantity}
            onChange={(value) => requestUpdateProductInCart({ cart_item_uuid: uuid, quantity: value })}
            size="sm"
        />
    );
};

interface ModifyCartItemProps {
    uuid: string;
    compact?: boolean;
    setModal: (uuid: string) => void;
}

const ModifyCartItem: FC<ModifyCartItemProps> = ({ uuid, compact, setModal }) => {
    const { t } = useTranslation();

    const translations = {
        modifyItemLabel: t('product-table-component.modify-cart-item'),
    };

    return (
        <RoundIcon
            variant={RoundIconVariants.variant2}
            icon="fa-light fa-pen-to-square"
            onClick={() => setModal(uuid)}
            outsideText={compact ? undefined : translations.modifyItemLabel}
        />
    );
};

interface RemoveCartItemProps {
    uuid: string;
    compact?: boolean;
}

const RemoveCartItem: FC<RemoveCartItemProps> = ({ uuid, compact }) => {
    const { request: requestUpdateProductInCart, loading } = useUpdateProductInCart();
    const { t } = useTranslation();

    const translations = {
        removeItemLabel: t('product-table-component.remove-cart-item'),
    };

    const handleOnClick = () => {
        // prevent calling service when it was already called
        if (!loading) {
            requestUpdateProductInCart({ cart_item_uuid: uuid, quantity: 0 });
        }
    };

    return (
        <RoundIcon
            variant={RoundIconVariants.variant2}
            icon="fa-light fa-trash-can"
            onClick={handleOnClick}
            outsideText={compact ? undefined : translations.removeItemLabel}
        />
    );
};
